import React from 'react';
import { Button } from '@THC/components/Button';
import { Checkbox } from '@THC/components/Checkbox';
import { Textarea } from '@THC/components/Textarea';
import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import ModalFooter from 'Components/Modal/Footer/ModalFooter';
import { triggerSHFeedbackModalSubmittedEvent } from 'Services/analytics/events';
import { sendNegativeFeedback } from 'Services/api/serviceHistoryFeedback';

import texts from './ServiceHistoryFeedback.texts';

import styles from './ServiceHistoryFeedback.module.scss';

const LocalT = new MDText(texts);

interface ServiceHistoryBetaFeedbackFormProps {
	enquiryId: number;
	onDismissModal?: () => void;
}

export const ServiceHistoryFeedbackForm: React.FC<ServiceHistoryBetaFeedbackFormProps> = ({
	enquiryId,
	onDismissModal,
}) => {
	const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const formData = Object.fromEntries(new FormData(e.target as HTMLFormElement));

		const { freeformEntry, incorrectDetails, isMissing, someOtherFeedback, wrongCategory, wrongGap } =
			formData as Record<string, string>;

		const selectedFeedback = [
			wrongCategory,
			incorrectDetails,
			wrongGap,
			isMissing,
			someOtherFeedback,
			freeformEntry ? freeformEntry?.trim()?.replaceAll('\n', ' ') : '',
		]
			.filter(Boolean)
			.join('\n');

		try {
			await sendNegativeFeedback(enquiryId, selectedFeedback);
			triggerSHFeedbackModalSubmittedEvent(enquiryId);
			toast({
				content: LocalT.translate('toast.success.content'),
				heading: LocalT.translate('toast.success.heading'),
				variant: 'success',
			});
			onDismissModal?.();
		} catch {
			toast({ content: LocalT.translate('toast.error'), variant: 'error' });
			onDismissModal?.();
		}
	};

	return (
		<section className={styles.form}>
			<p className={styles.formHeading}>{LocalT.translate('form.heading')}</p>
			<form onSubmit={onFormSubmit}>
				<section>
					<Checkbox
						formProps={{
							input: {
								className: styles.checkbox,
								name: 'wrongCategory',
								value: LocalT.translate('form.reasons.wrongCategory'),
							},
						}}
						id="wrongCategory"
						label={LocalT.translate('form.reasons.wrongCategory')}
					/>
					<Checkbox
						formProps={{
							input: {
								className: styles.checkbox,
								name: 'incorrectDetails',
								value: LocalT.translate('form.reasons.incorrectDetails'),
							},
						}}
						id="incorrectDetails"
						label={LocalT.translate('form.reasons.incorrectDetails')}
					/>
					<Checkbox
						formProps={{
							input: {
								className: styles.checkbox,
								name: 'wrongGap',
								value: LocalT.translate('form.reasons.wrongGap'),
							},
						}}
						id="wrongGap"
						label={LocalT.translate('form.reasons.wrongGap')}
					/>
					<Checkbox
						formProps={{
							input: {
								className: styles.checkbox,
								name: 'isMissing',
								value: LocalT.translate('form.reasons.isMissing'),
							},
						}}
						id="isMissing"
						label={LocalT.translate('form.reasons.isMissing')}
					/>
					<Checkbox
						formProps={{
							input: {
								className: styles.checkbox,
								name: 'someOtherFeedback',
								value: LocalT.translate('form.reasons.someOtherFeedback'),
							},
						}}
						id="someOtherFeedback"
						label={LocalT.translate('form.reasons.someOtherFeedback')}
					/>
				</section>
				<section className={styles.feedbackTextarea}>
					<Textarea
						formProps={{
							input: {
								name: 'freeformEntry',
							},
						}}
						id="freeformEntry"
						label={LocalT.translate('form.freeformEntry')}
						maxLength={150}
						placeholder={LocalT.translate('form.freeformEntry')!.toString()}
					/>
				</section>
				<section className={styles.feedbackSubmit}>
					<ModalFooter>
						<Button label={LocalT.translate('form.submit')} type="submit" />
					</ModalFooter>
				</section>
			</form>
		</section>
	);
};
